import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const megaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (megaRef.current && !megaRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-background-50/90 backdrop-blur-xl shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="mx-auto max-w-[1440px] px-4 md:px-6">
          <div className={`flex items-center justify-between transition-all duration-500 ${
            isScrolled ? 'h-16' : 'h-20'
          }`}>
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-900 cursor-pointer"
              aria-label="Menu"
            >
              <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>

            {/* Logo */}
            <a
              href="/"
              className="flex items-center gap-2 no-underline cursor-pointer"
            >
              <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center">
                <i className="ri-file-list-3-line text-background-50 text-lg"></i>
              </div>
              <span className="font-heading font-bold text-lg text-foreground-900 whitespace-nowrap">
                OOPBuy
              </span>
            </a>

            {/* Desktop nav items */}
            <div className="hidden md:flex items-center gap-3">
              {/* Home button */}
              <a
                href="/"
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-4-line"></i>
                <span>Startseite</span>
              </a>

              {/* Shopping Guide button */}
              <a
                href="/artikel/oopbuy-spreadsheet-guide"
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-book-open-line"></i>
                <span>Einkaufsratgeber</span>
              </a>

              {/* Mega menu trigger */}
              <div ref={megaRef} className="relative">
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-grid-line"></i>
                  <span>{t('navbar.categories')}</span>
                  <i className={`ri-arrow-down-s-line text-xs transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {/* Mega menu panel */}
                {isOpen && (
                  <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-[760px] bg-background-50/95 backdrop-blur-xl rounded-2xl border border-background-200/70 shadow-lg p-6 animate-fade-in">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                      {categories.map((cat) => (
                        <a
                          key={cat.slug}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap group"
                        >
                          <div className="w-11 h-11 rounded-xl overflow-hidden flex-shrink-0 bg-background-200">
                            <img
                              src={cat.thumbUrl}
                              alt={cat.name}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                          <span className="group-hover:translate-x-0.5 transition-transform">{cat.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* CTA buttons */}
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5"
              >
                oopbuy spreadsheet
                <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2.5 rounded-full border-2 border-primary-500 text-primary-500 text-sm font-semibold hover:bg-primary-500 hover:text-background-50 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5"
              >
                buy it now
                <i className="ri-shopping-cart-line text-sm"></i>
              </a>
            </div>

            {/* Mobile CTA */}
            <div className="md:hidden flex items-center gap-2">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold cursor-pointer whitespace-nowrap"
              >
                buy now
              </a>
            </div>
          </div>
        </div>

        {/* Mobile menu - improved with images and sections */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50/95 backdrop-blur-xl border-t border-background-200/70 animate-fade-in">
            <div className="px-4 py-4 max-h-[70vh] overflow-y-auto">
              {/* Quick links */}
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-background-200/70">
                <a
                  href="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-background-100 text-sm font-medium text-foreground-900 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-home-4-line"></i>
                  Startseite
                </a>
                <a
                  href="/artikel/oopbuy-spreadsheet-guide"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-background-100 text-sm font-medium text-foreground-900 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-book-open-line"></i>
                  Ratgeber
                </a>
              </div>

              {/* Category heading */}
              <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 px-1">
                Kategorien
              </p>

              {/* Categories with images */}
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2.5 p-2.5 rounded-xl text-xs text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                      <img
                        src={cat.thumbUrl}
                        alt={cat.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <span className="leading-tight">{cat.name}</span>
                  </a>
                ))}
              </div>

              {/* CTA at bottom */}
              <div className="pt-4 mt-4 border-t border-background-200/70 space-y-2">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-1.5 w-full px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  oopbuy spreadsheet
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </a>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-1.5 w-full px-5 py-3 rounded-full border-2 border-primary-500 text-primary-500 text-sm font-semibold hover:bg-primary-500 hover:text-background-50 transition-all cursor-pointer whitespace-nowrap"
                >
                  buy it now
                  <i className="ri-shopping-cart-line text-sm"></i>
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Spacer */}
      <div className="h-20 md:h-20"></div>
    </>
  );
}